import useCallAxios from "./useCallAxios";
import { useTransactionsData } from "./Transactions";
import api from "../Services/api";
import { parsePartnerPixError } from "../Utils/parsePartnerPixError";

/** Initiates a Pix transfer/payment (preview step) via Mobile IB → BaaS. */
export const initiatePixTransfer = async ({ payload }) => {
  try {
    const res = await api.post("/v1/ib/pix", payload);
    return { success: true, data: res.data?.data ?? res.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.information || err.response?.data?.detail || "Não foi possível processar a transferência",
      description: err.response?.data?.description || err.response?.data?.title || "Tente novamente.",
    };
  }
};

/** Confirms a Pix refund (devolução) for a received Pix transaction. */
export const confirmPixRefund = async ({
  operation_id,
  pix_id,
  end_to_end_id,
  amount,
  password_combination,
}) => {
  try {
    const res = await api.delete("/v1/ib/pix", {
      params: {
        operation_id,
        pix_id,
        end_to_end_id,
        amount,
      },
      data: {
        validation: { password_combination },
      },
    });
    return { success: true, data: res.data?.data ?? res.data };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.information || err.response?.data?.detail || "Não foi possível devolver o Pix",
      description: err.response?.data?.description || "Tente novamente.",
    };
  }
};

/** Registers a 4-digit transactional password when the person is not yet qualified. */
export const registerTransactionalPassword = async ({ password, confirmPassword }) => {
  try {
    const res = await api.post("/v1/ib/accounts/transactional_password", {
      password,
      confirm_password: confirmPassword,
    });
    return {
      success: true,
      data: res.data?.data ?? res.data,
      transactionalPasswordQualified:
        res.data?.transactional_password_qualified === true
        || res.data?.data?.transactional_password_qualified === true,
    };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.information || err.response?.data?.detail || "Aviso",
      description:
        err.response?.data?.description
        || "Não foi possível cadastrar a senha transacional.",
      error: err.response?.data?.error,
    };
  }
};

/** Changes an existing transactional password (current pair keypad + new password ×2). */
export const changeTransactionalPassword = async ({
  passwordCombination,
  password,
  confirmPassword,
}) => {
  try {
    const res = await api.put("/v1/ib/accounts/transactional_password", {
      password_combination: passwordCombination,
      password,
      confirm_password: confirmPassword,
    });
    return {
      success: true,
      data: res.data?.data ?? res.data,
      transactionalPasswordQualified:
        res.data?.transactional_password_qualified === true
        || res.data?.data?.transactional_password_qualified === true,
    };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.information || err.response?.data?.detail || "Aviso",
      description:
        err.response?.data?.description
        || "Não foi possível alterar a senha transacional.",
      error: err.response?.data?.error,
    };
  }
};

/** Verifies transactional password (pair keypad) without changing it. */
export const verifyTransactionalPassword = async ({ passwordCombination }) => {
  try {
    const res = await api.post("/v1/ib/accounts/transactional_password/verify", {
      password_combination: passwordCombination,
    });
    return {
      success: true,
      data: res.data?.data ?? res.data,
      valid: res.data?.valid === true || res.data?.data?.valid === true,
    };
  } catch (err) {
    return {
      success: false,
      message: err.response?.data?.information || err.response?.data?.detail || "Aviso",
      description:
        err.response?.data?.description
        || "Senha transacional inválida.",
      error: err.response?.data?.error,
    };
  }
};

/** Prefer BaaS/proxy detail fields; do not mask Rabbit/orchestrator failures as bad password. */
const pixConfirmErrorCopy = (data) => {
  const error = data?.error;
  const isPasswordError =
    error === "invalid_password" ||
    error === "password_not_set" ||
    error === "password_mismatch" ||
    error === "invalid_combination";

  const parsed = parsePartnerPixError(data);
  const message = parsed.message
    || (isPasswordError ? "Senha transacional inválida." : "Aviso");

  return {
    // PasswordConfirm shows title + description — keep description empty to avoid duplicate text.
    message,
    description: null,
    error: parsed.code || error,
  };
};

export const createPixBatch = async ({ key, amount, free_field, account_id }) => {
  try {
    const res = await api.post("/v1/ib/pix/batches", {
      key,
      amount,
      description: free_field,
      account_id,
    });
    return { success: true, data: res.data?.data ?? res.data };
  } catch (err) {
    const copy = pixConfirmErrorCopy(err.response?.data);
    return {
      success: false,
      message: copy.message,
      description: copy.description,
      error: copy.error,
    };
  }
};

export const confirmPixBatch = async ({ batch_id, password_combination, account_id }) => {
  try {
    const res = await api.post(`/v1/ib/pix/batches/${encodeURIComponent(batch_id)}/confirm`, {
      validation: { password_combination },
      account_id,
    });
    return { success: true, data: res.data?.data ?? res.data };
  } catch (err) {
    const copy = pixConfirmErrorCopy(err.response?.data);
    return {
      success: false,
      message: copy.message,
      description: copy.description,
      error: copy.error,
    };
  }
};

/** Confirms a Pix transfer/payment after password validation. */
export const confirmPixTransfer = async ({
  pix_id,
  amount,
  password_combination,
  documentToValidate,
}) => {
  try {
    const res = await api.put(`/v1/ib/pix/${pix_id}`, {
      validation: { password_combination },
      amount,
      documentToValidate,
    });
    return { success: true, data: res.data?.data ?? res.data };
  } catch (err) {
    const copy = pixConfirmErrorCopy(err.response?.data);
    return {
      success: false,
      message: copy.message,
      description: copy.description,
      error: copy.error,
    };
  }
};

/**
 * Creates a Pix QR charge via Mobile IB → BaaS.
 * - `qr_type: "dynamic"` (default) → POST `/v1/ib/pix/charge` → BaaS `…/qrcodes/dynamic`
 * - `qr_type: "static"` → POST `/v1/ib/pix/charge/static` → BaaS `…/qrcodes/static`
 */
export const createPixQrCharge = async ({
  amount,
  address_key,
  pix_key,
  key,
  due_date,
  expires_at,
  expiration,
  validity_after_expiration,
  reusable,
  description,
  qr_type = "dynamic",
}) => {
  try {
    const addressKey =
      (typeof address_key === "string" && address_key.trim()) ||
      (typeof pix_key === "string" && pix_key.trim()) ||
      (typeof key === "string" && key.trim()) ||
      (typeof key?.value === "string" && key.value.trim()) ||
      "";

    const cents = Number(amount) || 0;
    const isStatic = String(qr_type || "dynamic").toLowerCase() === "static";

    if (!addressKey) {
      return {
        success: false,
        message: "Selecione uma chave Pix",
        description: "A chave é obrigatória para gerar a cobrança.",
      };
    }
    if (!isStatic && cents <= 0) {
      return {
        success: false,
        message: "Informe o valor da cobrança",
        description: "Informe um valor maior que zero para gerar a cobrança.",
      };
    }

    if (isStatic) {
      const staticBody = { address_key: addressKey };
      if (cents > 0) {
        staticBody.amount = cents;
      }
      if (typeof description === "string" && description.trim()) {
        staticBody.description = description.trim();
      }

      const res = await api.post("/v1/ib/pix/charge/static", staticBody);
      const data = res.data?.data ?? res.data;
      return {
        success: true,
        data: {
          qrcode: data?.qrcode || data?.qr_code || data?.emv || "",
          image_base64: data?.image_base64 || data?.imageBase64 || "",
          amount: data?.amount ?? cents,
          pixId: data?.pixId || data?.pix_id || data?.qr_code_id,
          address_key: data?.address_key || data?.pix_key || addressKey,
          type: data?.type || "static",
          ...data,
        },
      };
    }

    const dueDate =
      (typeof due_date === "string" && due_date.trim()) ||
      (typeof expires_at === "string" && expires_at.trim()) ||
      (typeof expiration === "string" && expiration.trim()) ||
      "";

    const body = {
      address_key: addressKey,
      amount: cents,
      reusable: Boolean(reusable),
    };
    if (dueDate) {
      body.due_date = dueDate.slice(0, 10);
    }
    if (
      validity_after_expiration !== undefined &&
      validity_after_expiration !== null &&
      String(validity_after_expiration).trim() !== ""
    ) {
      body.validity_after_expiration = String(validity_after_expiration).trim();
    }
    if (typeof description === "string" && description.trim()) {
      body.description = description.trim();
    }

    const res = await api.post("/v1/ib/pix/charge", body);
    const data = res.data?.data ?? res.data;
    return {
      success: true,
      data: {
        qrcode: data?.qrcode || data?.qr_code || data?.emv || "",
        image_base64: data?.image_base64 || data?.imageBase64 || "",
        amount: data?.amount ?? cents,
        pixId: data?.pixId || data?.pix_id || data?.qr_code_id,
        address_key: data?.address_key || data?.pix_key || addressKey,
        type: data?.type || "dynamic",
        ...data,
      },
    };
  } catch (err) {
    const detail =
      err.response?.data?.information ||
      err.response?.data?.detail ||
      "Não foi possível gerar o QR Code";
    const description = err.response?.data?.description;
    return {
      success: false,
      message: detail,
      // Avoid duplicating the same long BaaS hint as title + body in the progress overlay.
      description:
        description && description !== detail ? description : "Tente novamente.",
    };
  }
};

const filterQuery = (values) => ({ ...values });

/** MobileService IB responses often nest payload under `{ data: { ... } }`. */
const unwrapApiPayload = (requestData) =>
  requestData?.data?.data ?? requestData?.data;

//POST
export const CreatePixQRCode = () => {
  const context = `createPixQRCode`;
  const { reset, requestSuccess, requestData, requestLoading, fetchPOST } =
    useCallAxios({
      context,
    });

  const handlePOST = (params) => {
    fetchPOST({ body: params });
  };

  const state = unwrapApiPayload(requestData);

  return {
    reset,
    loading: requestLoading,
    state,
    success: requestSuccess,
    handlePOST,
  };
};

//POST
export const InitiatePixPayment = () => {
  const context = `pixPayment`;
  const {
    reset,
    requestSuccess,
    requestData,
    requestLoading,
    requestError,
    requestMessage,
    fetchPOST,
  } = useCallAxios({
    context,
  });

  const handlePOST = (params) => {
    fetchPOST({ body: params });
  };

  const state = unwrapApiPayload(requestData);

  return {
    reset,
    loading: requestLoading,
    state,
    success: requestSuccess,
    error: requestError,
    message: requestMessage,
    handlePOST,
  };
};

//PUT
export const PixPaymentConfirmation = () => {
  const context = `confirmPix`;
  const { reset, requestSuccess, requestData, requestLoading, fetchPUT } =
    useCallAxios({
      context,
    });
  const state = unwrapApiPayload(requestData);

  const handlePUT = (params) => {
    const { payload, pix_id } = params;
    fetchPUT({ body: payload, id: pix_id });
  };
  //const state = normalizeContactsData(requestData);
  return {
    reset,
    loading: requestLoading,
    state,
    success: requestSuccess,
    handlePUT,
  };
};

//PIX KEYS
export const ListPixKeys = () => {
  const context = "pixKeys";
  const { reset, requestSuccess, requestData, requestLoading, fetchGET } =
    useCallAxios({ context });

  const handleGET = (params = {}) => {
    fetchGET({ queryParameters: params });
  };

  return {
    reset,
    loading: requestLoading,
    state: requestData?.data,
    success: requestSuccess,
    handleGET,
  };
};

export const GetPixKey = () => {
  const context = "pixKeyDetail";
  const { reset, requestSuccess, requestData, requestLoading, fetchGET } =
    useCallAxios({ context });

  const handleGET = (keyId) => {
    fetchGET({ id: keyId });
  };

  return {
    reset,
    loading: requestLoading,
    state: requestData?.data,
    success: requestSuccess,
    handleGET,
  };
};

export const CreatePixKey = () => {
  const context = "pixKeyCreate";
  const {
    reset,
    requestSuccess,
    requestData,
    requestLoading,
    requestError,
    requestMessage,
    fetchPOST,
  } = useCallAxios({ context });

  const handlePOST = (body) => {
    fetchPOST({ body });
  };

  return {
    reset,
    loading: requestLoading,
    state: requestData?.data,
    success: requestSuccess,
    error: requestError,
    message: requestMessage,
    handlePOST,
  };
};

export const DeletePixKey = () => {
  const context = "pixKeyDelete";
  const {
    reset,
    requestSuccess,
    requestData,
    requestLoading,
    requestError,
    requestMessage,
    fetchDELETE,
  } = useCallAxios({ context });

  const handleDELETE = (keyId) => {
    fetchDELETE({ id: keyId });
  };

  return {
    reset,
    loading: requestLoading,
    state: requestData?.data,
    success: requestSuccess,
    error: requestError,
    message: requestMessage,
    handleDELETE,
  };
};

/** Pix Área statement — same Extrato feed (`/v1/ib/accounts/statements`), filtered to Pix client-side. */
export const usePixTransactionsData = () => {
  const {
    reset,
    loading,
    state,
    success,
    handleGET,
  } = useTransactionsData();

  return {
    reset,
    loading,
    state,
    success,
    handleGET,
  };
};

/** @deprecated Prefer usePixTransactionsData (Extrato statements filtered to Pix). */
export const GetPixTransactions = usePixTransactionsData;

//DELETE
export const ReturnPix = () => {
  const context = `returnPix`;
  const { reset, requestSuccess, requestData, requestLoading, fetchDELETE } =
    useCallAxios({
      context,
    });
  const state = unwrapApiPayload(requestData);
  const handleReturnPix = (params) => {
    fetchDELETE({ context, queryParameters: filterQuery(params) });
  };

  return {
    reset,
    loading: requestLoading,
    state,
    success: requestSuccess,
    handleReturnPix,
  };
};
